import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { LayoutData } from '@/types'

import { Clock, MapPin, Phone, Truck } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Breadcrumbs } from '@/components/generals/breadcrumbs'
import { SectionHeading } from '@/components/generals/section-heading'
import { InquiryForm } from '@/components/forms/inquiry-form'
import { getLayoutData } from '@/lib/catalog'
import { contactInfo } from '@/constants/site'

interface ContactPageProps {
    layout: LayoutData
}

const ContactPage: FC<ContactPageProps> = ({ layout }): ReactNode => {
    return (
        <Layout categories={layout.categories}>
            <Seo
                title='تماس با ما'
                description='برای مشاورهٔ خرید مبلمان، سفارش در ابعاد و رنگ دلخواه یا هماهنگی بازدید از شوروم نارون با ما تماس بگیرید یا فرم درخواست را پر کنید.'
                ogImage={layout.shareImage}
            />

            <section aria-labelledby='contact-title' className='pb-20 pt-8 lg:pb-28 lg:pt-12'>
                <Container>
                    <Breadcrumbs
                        items={[
                            { key: 'home', label: 'خانه', href: '/' },
                            { key: 'contact', label: 'تماس با ما' }
                        ]}
                    />

                    <SectionHeading
                        as='h1'
                        eyebrow='تماس با نارون'
                        title='با ما در ارتباط باشید'
                        titleId='contact-title'
                        description='برای مشاورهٔ خرید، سفارش اختصاصی یا هماهنگی بازدید از شوروم، فرم را پر کنید یا مستقیم تماس بگیرید. معمولاً در کمتر از یک روز کاری پاسخ می‌دهیم.'
                        className='mt-8'
                    />

                    <div className='mt-14 grid gap-10 lg:grid-cols-12 lg:gap-12'>
                        <ul className='grid content-start gap-4 lg:col-span-5'>
                            <li className='flex gap-5 rounded-3xl border border-border bg-card p-6'>
                                <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-brand'>
                                    <Phone className='size-5' aria-hidden='true' />
                                </span>
                                <div>
                                    <h2 className='text-sm text-muted-foreground'>
                                        تلفن شوروم
                                    </h2>
                                    <a href={contactInfo.phoneHref} className='mt-1.5 block text-xl font-semibold text-foreground transition-colors hover:text-brand'>
                                        <span dir='ltr'>
                                            {contactInfo.phone}
                                        </span>
                                    </a>
                                    <p className='mt-1 text-sm text-muted-foreground'>
                                        پاسخ‌گویی در ساعات کاری شوروم
                                    </p>
                                </div>
                            </li>

                            <li className='flex gap-5 rounded-3xl border border-border bg-card p-6'>
                                <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-brand'>
                                    <MapPin className='size-5' aria-hidden='true' />
                                </span>
                                <div>
                                    <h2 className='text-sm text-muted-foreground'>
                                        آدرس شوروم
                                    </h2>
                                    <p className='mt-1.5 text-base leading-8 text-foreground'>
                                        {contactInfo.address}
                                    </p>
                                    <a
                                        href={contactInfo.mapUrl}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='mt-1 inline-block text-sm font-medium text-brand underline decoration-brand/40 underline-offset-8 transition-colors hover:decoration-brand'
                                    >
                                        مسیریابی در نقشه
                                    </a>
                                </div>
                            </li>

                            <li className='flex gap-5 rounded-3xl border border-border bg-card p-6'>
                                <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-brand'>
                                    <Clock className='size-5' aria-hidden='true' />
                                </span>
                                <div>
                                    <h2 className='text-sm text-muted-foreground'>
                                        ساعات کاری
                                    </h2>
                                    <p className='mt-1.5 text-base leading-8 text-foreground'>
                                        {contactInfo.hours}
                                    </p>
                                    <p className='text-sm text-muted-foreground'>
                                        {contactInfo.closedDays}
                                    </p>
                                </div>
                            </li>

                            <li className='flex gap-5 rounded-3xl border border-border bg-card p-6'>
                                <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-brand'>
                                    <Truck className='size-5' aria-hidden='true' />
                                </span>
                                <div>
                                    <h2 className='text-sm text-muted-foreground'>
                                        ارسال به سراسر کشور
                                    </h2>
                                    <p className='mt-1.5 text-base leading-8 text-foreground'>
                                        تهران و کرج رایگان، سایر شهرها با بسته‌بندی مقاوم
                                    </p>
                                </div>
                            </li>
                        </ul>

                        <div id='inquiry' className='rounded-3xl border border-border bg-card p-6 sm:p-10 lg:col-span-7'>
                            <h2 className='font-heading text-3xl font-semibold text-foreground'>
                                فرم درخواست
                            </h2>

                            <p className='mt-2 text-sm leading-7 text-muted-foreground'>
                                چند خط دربارهٔ چیزی که در ذهن دارید بنویسید؛ مشاوران ما با شما تماس می‌گیرند.
                            </p>

                            <InquiryForm className='mt-8' />
                        </div>
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ContactPageProps> = async () => {
    return {
        props: { layout: await getLayoutData() },
        revalidate: 300
    }
}

export default ContactPage
